import React, { useEffect, useState } from "react";

/**
 * MobileShell: app-like frame for small screens
 * - Hides top app bar on Home (no top logo)
 * - Shows only language toggle on other pages
 * - Bottom tab bar bound to your hash routes
 */
export default function MobileShell({
  t,
  lang,
  setLang,
  dir = "ltr",
  routes = {},
  UI = { garnet: "#8B1E3F", teal: "#008b8b", border: "#e6e8eb" },
  children,
}) {
  const getHash = () =>
    (typeof window !== "undefined" && window.location.hash) ||
    routes.home ||
    "#home";

  const [hash, setHash] = useState(getHash());

  useEffect(() => {
    const onHash = () => setHash(getHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isHome = hash === (routes.home || "#home");

  const tabs = [
    { key: "home",     label: t.nav.home,     hash: routes.home,     icon: HomeIcon },
    { key: "floors",   label: t.nav.floors,   hash: routes.floors,   icon: GridIcon },
    { key: "location", label: t.nav.location, hash: routes.location, icon: PinIcon },
    { key: "gallery",  label: t.nav.gallery,  hash: routes.gallery,  icon: ImageIcon },
    { key: "contact",  label: t.nav.contact,  hash: routes.contact,  icon: PhoneIcon },
  ];

  const toggleLang = () => {
    const next = lang === "ar" ? "en" : "ar";
    try { localStorage.setItem("granada_lang", next); } catch {}
    setLang(next);
  };

  const safeBottom = "env(safe-area-inset-bottom, 0px)";

  return (
    <div
      dir={dir}
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100svh",
        background: "#fff",
        color: "#111",
      }}
    >
      {/* Top App Bar — hidden on Home */}
      <header
        style={{
          display: isHome ? "none" : "flex",
          position: "sticky",
          top: 0,
          zIndex: 50,
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "10px 14px",
          borderBottom: `1px solid ${UI.border}`,
          background: "#fff",
          gap: 8,
        }}
      >
        <button
          onClick={toggleLang}
          style={{
            borderRadius: 10,
            border: `1px solid ${UI.border}`,
            background: "#fff",
            padding: "6px 10px",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          {t.nav.toggle}
        </button>
      </header>

      {/* Content */}
      <main style={{ flex: 1, minHeight: 0 }}>
        {/* padding so content isn't hidden behind tabs */}
        <div style={{ padding: "12px 12px calc(56px + " + safeBottom + ")" }}>
          {children}
        </div>
      </main>

      {/* Bottom Tab Bar */}
      <nav
        aria-label={dir === "rtl" ? "التنقل السفلي" : "Mobile navigation"}
        style={{
          position: "sticky",
          bottom: 0,
          zIndex: 40,
          borderTop: `1px solid ${UI.border}`,
          background: "#fff",
          padding: `6px 6px calc(6px + ${safeBottom})`,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 4,
          }}
        >
          {tabs.map(({ key, label, hash: h, icon: Icon }) => {
            const active = hash === h;
            return (
              <a
                key={key}
                href={h}
                aria-current={active ? "page" : undefined}
                style={{
                  textDecoration: "none",
                  border: "none",
                  background: "transparent",
                  padding: "6px 4px",
                  borderRadius: 10,
                  color: active ? UI.garnet : "#444",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 11,
                  textAlign: "center",
                }}
              >
                <Icon filled={active} color={active ? UI.garnet : "#666"} />
                <span style={{ lineHeight: 1 }}>{label}</span>
              </a>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

/* --- Tiny inline icons (no external deps) --- */
function HomeIcon({ color = "#666", filled }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M3 10.5 12 4l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9.5Z"
            stroke={color} strokeWidth="1.5" fill={filled ? color + "22" : "none"} />
    </svg>
  );
}
function GridIcon({ color = "#666", filled }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="3" width="8" height="8" rx="1.5"
            stroke={color} strokeWidth="1.5" fill={filled ? color + "22" : "none"} />
      <rect x="13" y="3" width="8" height="8" rx="1.5"
            stroke={color} strokeWidth="1.5" fill={filled ? color + "22" : "none"} />
      <rect x="3" y="13" width="8" height="8" rx="1.5"
            stroke={color} strokeWidth="1.5" fill={filled ? color + "22" : "none"} />
      <rect x="13" y="13" width="8" height="8" rx="1.5"
            stroke={color} strokeWidth="1.5" fill={filled ? color + "22" : "none"} />
    </svg>
  );
}
function PinIcon({ color = "#666", filled }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 22s7-6.1 7-12a7 7 0 1 0-14 0c0 5.9 7 12 7 12Z"
            stroke={color} strokeWidth="1.5" fill={filled ? color + "22" : "none"} />
      <circle cx="12" cy="10" r="2.5" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}
function ImageIcon({ color = "#666", filled }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2"
            stroke={color} strokeWidth="1.5" fill={filled ? color + "22" : "none"} />
      <path d="M7 15l3-3 5 5" stroke={color} strokeWidth="1.5" />
      <circle cx="9" cy="9" r="1.5" fill={color} />
    </svg>
  );
}
function PhoneIcon({ color = "#666", filled }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M6 3h6l2 4-3 2a12 12 0 0 0 4 4l2-3 4 2v6a2 2 0 0 1-2 2c-9 0-16-7-16-16a2 2 0 0 1 2-2Z"
            stroke={color} strokeWidth="1.5" fill={filled ? color + "22" : "none"} />
    </svg>
  );
}
